// The report engine: year file in, analysis out. Deterministic: the same records in any order give the same result.
import { seTax, type TraceStep } from "../calc.ts";
import { D, type Dec, ZERO } from "../money.ts";
import { RULES_2026 } from "../rules/2026.ts";
import { analyzeFutures, screeningIssues, type FuturesReconciliation, type FuturesResult } from "./futures.ts";
import { IssueList, m, resultFrom, type Issue, type ModuleResult } from "./issues.ts";
import { analyzeProp, type PropReconciliation, type PropResult } from "./prop.ts";
import { currentDecisions, resolveSources, type ExclusionReason } from "./sources.ts";
import type { Id, Money, YearFile } from "./types.ts";
import { known, type Amount } from "./values.ts";

export const ENGINE_VERSION = "ftr-report-engine/0.1.0";
export const RULES_VERSION = `federal-${RULES_2026.taxYear}/2026-10-17`;

export interface SeView {
  netProfit: Money;
  netEarnings: Money;
  socialSecurityTax: Money;
  medicareTax: Money;
  seTax: Money;
  deductibleHalf: Money;
}

export interface Analysis {
  engineVersion: string;
  rulesVersion: string;
  taxYear: number;
  excludedSources: { sourceId: Id; reason: ExclusionReason }[];
  issues: Issue[];
  modules: {
    section1256: ModuleResult<FuturesResult>;
    propBusiness: Record<Id, ModuleResult<PropResult>>;
    /** A cross-check for filing software, which works out SE tax itself. Never an amount to add. */
    selfEmployment: Record<Id, ModuleResult<SeView>>;
  };
  reconciliation: { futures: FuturesReconciliation[]; prop: PropReconciliation[] };
  /** Federal income tax withheld on the forms (box 4). A payment toward tax, never income. */
  withholding: { personId: Id | null; sourceId: Id; amount: Money }[];
}

export function analyze(yf: YearFile): Analysis {
  const issues = new IssueList();
  if (yf.taxYear !== RULES_2026.taxYear)
    issues.add({
      code: "YEAR_NOT_SUPPORTED",
      severity: "blocker",
      module: "file",
      personId: null,
      refs: [],
      message: `Reports for ${yf.taxYear} aren't available yet.`,
      nextAction: "Check back once that year's rules are released.",
    });
  checkHousehold(yf, issues);

  const decisions = currentDecisions(yf, issues);
  const view = resolveSources(yf, decisions, issues);
  const futures = analyzeFutures(yf, view, decisions, issues);

  const people = [...yf.people].sort((a, b) => (a.id < b.id ? -1 : 1));
  const propBusiness: Record<Id, ModuleResult<PropResult>> = {};
  const selfEmployment: Record<Id, ModuleResult<SeView>> = {};
  const propRecon: PropReconciliation[] = [];
  const withholding: Analysis["withholding"] = [];
  for (const p of people) {
    const prop = analyzeProp(yf, p.id, view, decisions, issues);
    propRecon.push(...prop.reconciliation);
    withholding.push(...prop.withholding.map((w) => ({ personId: p.id, ...w })));
    selfEmployment[p.id] = selfEmploymentFor(yf, p.id, prop.result, issues);
    // Re-read issues: the SE step can add to them, and the prop result should list everything about prop.
    propBusiness[p.id] = refresh(prop.result, issues.for("propBusiness", p.id));
  }
  for (const f of view.effectiveForms)
    if (f.kind === "form_1099b") {
      const w = known(f.box4);
      if (w && !w.isZero()) withholding.push({ personId: f.personId, sourceId: f.id, amount: m(w) });
    }

  // Household problems hold every module.
  const fileBlockers = issues.items.filter((i) => i.module === "file" && i.severity === "blocker");
  const hold = <T>(r: ModuleResult<T>): ModuleResult<T> =>
    fileBlockers.length && r.state !== "not_applicable" ? { state: "blocked", partial: "value" in r ? r.value : r.state === "blocked" ? r.partial : null, trace: r.trace, issues: [...fileBlockers, ...r.issues] } : r;

  return {
    engineVersion: ENGINE_VERSION,
    rulesVersion: RULES_VERSION,
    taxYear: yf.taxYear,
    excludedSources: view.excluded,
    issues: [...issues.items].sort((a, b) => severityRank(a) - severityRank(b) || (a.code < b.code ? -1 : a.code > b.code ? 1 : 0)),
    modules: {
      section1256: hold(refresh(futures.result, issues.for("section1256"))),
      propBusiness: Object.fromEntries(Object.entries(propBusiness).map(([k, v]) => [k, hold(v)])),
      selfEmployment: Object.fromEntries(Object.entries(selfEmployment).map(([k, v]) => [k, hold(v)])),
    },
    reconciliation: { futures: futures.reconciliation, prop: propRecon },
    withholding,
  };
}

function severityRank(i: Issue): number {
  return i.severity === "blocker" ? 0 : i.severity === "review" ? 1 : 2;
}

function refresh<T>(r: ModuleResult<T>, latest: Issue[]): ModuleResult<T> {
  if (r.state === "not_applicable") return r;
  const value = r.state === "blocked" ? r.partial : r.value;
  return resultFrom(value, r.trace, latest);
}

function checkHousehold(yf: YearFile, issues: IssueList) {
  const status = yf.household.filingStatus;
  const spouse = yf.people.some((p) => p.role === "spouse");
  const add = (code: string, message: string, nextAction: string) => issues.add({ code, severity: "blocker", module: "file", personId: null, refs: [], message, nextAction });
  if (!yf.people.some((p) => p.role === "primary")) add("NO_TAXPAYER", "Add yourself first.", "Enter your name.");
  if (status === null) add("FILING_STATUS_NEEDED", "We need your filing status.", "Choose your filing status.");
  else if (status === "mfj" && !spouse) add("SPOUSE_NEEDED", "A joint return needs your spouse added, even if they didn't trade.", "Add your spouse.");
  else if (spouse && status !== "mfj" && status !== "mfs")
    add("SPOUSE_STATUS_MISMATCH", "A spouse is listed, but the filing status isn't married.", "Change the filing status, or remove the spouse.");
}

function wageBase(a: Amount): Dec | null {
  if (a.state === "not_applicable") return new D(0);
  return known(a);
}

function selfEmploymentFor(yf: YearFile, personId: Id, prop: ModuleResult<PropResult>, issues: IssueList): ModuleResult<SeView> {
  if (prop.state === "not_applicable") return { state: "not_applicable", issues: [] };
  const add = (x: Omit<Issue, "module" | "personId">) => issues.add({ ...x, module: "selfEmployment", personId });
  screeningIssues(yf, personId, ["ownAccountTraderDeductions"], "selfEmployment", issues);
  const income = yf.income[personId];
  const ss = income ? wageBase(income.socialSecurityWages) : null;
  const other = income ? wageBase(income.otherSeEarnings) : null;
  if (ss === null)
    add({
      code: "SS_WAGES_NEEDED",
      severity: "blocker",
      refs: [personId],
      message: "We need this person's Social Security wages to work out self-employment tax.",
      nextAction: "Enter W-2 box 3 plus box 7 from every W-2 for this person, or say there were no W-2 jobs.",
    });
  if (other === null)
    add({
      code: "OTHER_SE_NEEDED",
      severity: "blocker",
      refs: [personId],
      message: "We need to know whether this person had other self-employment income.",
      nextAction: "Enter the net profit from any other business or 1099 work, or say there wasn't any.",
    });
  const propValue = prop.state === "blocked" ? prop.partial : prop.value;
  if (prop.state === "blocked")
    add({ code: "SE_WAITS_ON_PROP", severity: "blocker", refs: [personId], message: "Self-employment tax waits on the prop figures above.", nextAction: "Finish the prop section first." });
  else if (prop.state === "provisional")
    add({ code: "SE_PROVISIONAL", severity: "review", refs: [personId], message: "Self-employment tax uses prop figures that still need your review.", nextAction: "Finish the prop review items." });

  if (!propValue) return resultFrom<SeView>(null, [], issues.for("selfEmployment", personId));
  const netProfit = new D(propValue.netProfit).plus(other ?? ZERO);
  const se = seTax({ netProfit, socialSecurityWages: ss ?? ZERO });
  const v = se.value;
  const trace: TraceStep[] = [
    ...(other && !other.isZero() ? [{ label: "Other self-employment profit", value: other.toFixed(2) }] : []),
    ...se.trace,
  ];
  return resultFrom<SeView>(
    { netProfit: m(netProfit), netEarnings: m(v.netEarnings), socialSecurityTax: m(v.socialSecurityTax), medicareTax: m(v.medicareTax), seTax: m(v.seTax), deductibleHalf: m(v.deductibleHalf) },
    trace,
    issues.for("selfEmployment", personId),
  );
}
